<template>
  <v-expansion-panel
    ref="panelRef"
    :bg-color="_props.bgColor"
    :class="_props.class"
    class="border border-solid"
    :value="value"
  >
    <v-defaults-provider :defaults="_defaults">
      <v-expansion-panel-title
        ref="titleRef"
        :class="textColorClasses"
        :style="textColorStyles"
      >
        <template #default="{ expanded, expandIcon, collapseIcon }">
          <v-row
            no-gutters
            align="center"
            :class="[hasTitleDescription ? 'relative self-start' : '']"
          >
            <v-defaults-provider
              scoped
              :defaults="{ VIcon: { class: 'me-4', size: 24 } }"
            >
              <slot
                v-if="hasPrependIcon"
                name="prepend-icon"
              >
                <v-icon :icon="prependIcon" />
              </slot>
            </v-defaults-provider>
            <v-col>
              <slot
                name="title"
                :expanded="expanded"
              >
                {{ title }}
              </slot>
            </v-col>
            <v-icon :icon="expanded ? collapseIcon : expandIcon" />
            <v-slide-y-transition mode="out-in">
              <u-text
                v-if="showTitleDescription"
                ref="descriptionRef"
                :color="titleOptions?.descriptionColor"
                class="text-body-xxs font-normal pt-05 absolute top-full"
                :class="{ 'left-10': hasPrependIcon }"
              >
                <slot name="title-description">
                  {{ titleOptions?.description }}
                </slot>
              </u-text>
            </v-slide-y-transition>
          </v-row>
        </template>
      </v-expansion-panel-title>
    </v-defaults-provider>

    <v-expansion-panel-text>
      <slot>
        {{ text }}
      </slot>
    </v-expansion-panel-text>
  </v-expansion-panel>
</template>

<script setup lang="ts">
import '#ui-sass/components/VExpansionPanel/VExpansionPanel.scss'
import { useTextColor } from 'vuetify/lib/composables/color.mjs'

defineOptions({
  inheritAttrs: false,
})

defineSlots<{
  'title': (props: {
    expanded: boolean
  }) => void
  'title-description': () => void
  'prepend-icon': () => void
  'default': () => void
}>()

type TitleOptions = {
  description?: string
  descriptionColor?: string
}

const {
  bgColor = 'black-8',
  borderColor = 'border-transparent',
  activeBorderColor = 'border-black-8',
  titleColor = 'type-primary',
  hover = true,
  titleOptions,
  prependIcon,
} = defineProps<{
  text?: string
  title?: string
  value?: string
  prependIcon?: string
  bgColor?: string
  borderColor?: string
  titleColor?: string
  activeBorderColor?: string
  hover?: boolean
  titleOptions?: TitleOptions
}>()

const panelRef = useTemplateRef<Element>('panelRef')
const titleRef = useTemplateRef<Element>('titleRef')
const descriptionRef = useTemplateRef<Element>('descriptionRef')
const titleHeight = ref(convertToUnit(58))

const { textColorClasses, textColorStyles } = useTextColor(ref(titleColor))
const isHovered = useElementHover(panelRef)
const isActive = computed(() => panelRef.value?.groupItem.isSelected.value ?? false)
const _props = computed(() => ({
  bgColor: hover ? (isHovered.value ? bgColor : undefined) : bgColor,
  class: hover ? (isHovered.value || isActive.value ? activeBorderColor : borderColor) : borderColor,
}))

const slots = useSlots()
const hasPrependIcon = computed(() => !!(prependIcon || slots['prepend-icon']))
const hasTitleDescription = computed(() => !!(titleOptions?.description || slots['title-description']))
const showTitleDescription = computed(() => !isActive.value && hasTitleDescription.value)

const { height: descriptionHeight } = useElementBounding(descriptionRef)

const _defaults = computed(() => ({
  VExpansionPanelTitle: {
    style: {
      '--expansion-panel-title-min-height': titleHeight.value,
    },
  },
}))

useResizeObserver(panelRef, () => {
  const paddingTop = getComputedStyle(unrefElement(titleRef)).paddingTop
  const paddingBottom = getComputedStyle(unrefElement(titleRef)).paddingBottom
  const paddingVertical = parseInt(paddingTop, 10) + parseInt(paddingBottom, 10)

  titleHeight.value = convertToUnit(Math.max(58, 24 + paddingVertical + descriptionHeight.value))
})
</script>
